import React from 'react';
import { motion } from 'framer-motion';
import {
    Users as UsersIcon,
    TrendingUp,
    Calendar,
    Mail,
    Book,
    MessageSquare,
    Shield,
    Activity,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const AnalyticsSection = ({ users, subscribers, tutorials, contacts, notifications, isMobile }) => {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const today = now.toDateString();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const getDate = (u) => {
        if (!u.createdAt) return null;
        return u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
    };

    const dailyData = Array.from({ length: 14 }, (_, i) => {
        const d = new Date(now);
        d.setDate(d.getDate() - (13 - i));
        const label = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
        const count = users.filter(u => {
            const ud = getDate(u);
            return ud && ud.toDateString() === d.toDateString();
        }).length;
        return { day: label, Users: count };
    });

    const monthlyData = Array.from({ length: 12 }, (_, i) => {
        const d = new Date(thisYear, thisMonth - (11 - i), 1);
        const count = users.filter(u => {
            const ud = getDate(u);
            return ud && ud.getMonth() === d.getMonth() && ud.getFullYear() === d.getFullYear();
        }).length;
        return { month: MONTHS[d.getMonth()], Users: count };
    });

    const subMonthlyData = Array.from({ length: 12 }, (_, i) => {
        const d = new Date(thisYear, thisMonth - (11 - i), 1);
        const count = subscribers.filter(s => {
            const sd = s.timestamp instanceof Date ? s.timestamp : (s.timestamp?.toDate ? s.timestamp.toDate() : null);
            return sd && sd.getMonth() === d.getMonth() && sd.getFullYear() === d.getFullYear();
        }).length;
        return { month: MONTHS[d.getMonth()], Subscribers: count };
    });

    const roleData = [
        { name: 'Users', value: users.filter(u => !u.role || u.role === 'user').length, color: '#8b5cf6' },
        { name: 'Admin', value: users.filter(u => u.role === 'admin').length, color: '#10b981' },
        { name: 'Moderator', value: users.filter(u => u.role === 'moderator').length, color: '#f59e0b' },
    ].filter(r => r.value > 0);

    const todayUsers = users.filter(u => { const d = getDate(u); return d && d.toDateString() === today; }).length;
    const monthUsers = users.filter(u => { const d = getDate(u); return d && d.getMonth() === thisMonth && d.getFullYear() === thisYear; }).length;
    const lastMonthUsers = users.filter(u => {
        const d = getDate(u);
        const lm = new Date(thisYear, thisMonth - 1, 1);
        return d && d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear();
    }).length;
    const growth = lastMonthUsers > 0 ? Math.round(((monthUsers - lastMonthUsers) / lastMonthUsers) * 100) : 100;

    const tooltipStyle = {
        contentStyle: {
            background: 'var(--bg-card-solid)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            padding: '12px 16px',
            fontSize: '12px',
            fontWeight: 'bold',
            color: 'var(--text-main)'
        },
        itemStyle: { color: 'var(--text-main)' },
        labelStyle: { color: 'var(--text-muted)', marginBottom: '4px' },
        cursor: { fill: 'rgba(139, 92, 246, 0.06)' }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                {[
                    { label: 'Total Users', value: users.length, icon: <UsersIcon size={20} />, color: 'violet', sub: 'All Time' },
                    { label: 'New Today', value: todayUsers, icon: <TrendingUp size={20} />, color: 'emerald', sub: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) },
                    { label: 'This Month', value: monthUsers, icon: <Calendar size={20} />, color: 'cyan', sub: `${growth > 0 ? '+' : ''}${growth}% growth` },
                    { label: 'Subscribers', value: subscribers.length, icon: <Mail size={20} />, color: 'amber', sub: 'Newsletter' },
                    { label: 'Tutorials', value: tutorials.length, icon: <Book size={20} />, color: 'rose', sub: 'CodeCampus' },
                    { label: 'Messages', value: contacts.length, icon: <MessageSquare size={20} />, color: 'teal', sub: 'Contact Form' },
                ].map((kpi, i) => (
                    <motion.div key={kpi.label} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}>
                        <Card className="p-6 bg-card/40 backdrop-blur-xl border-border/40 rounded-3xl relative overflow-hidden group hover:scale-105 transition-all duration-300">
                            <div className={`absolute top-0 right-0 w-24 h-24 bg-${kpi.color}-500/10 rounded-full blur-2xl -mr-12 -mt-12 pointer-events-none group-hover:bg-${kpi.color}-500/20 transition-all`} />
                            <div className={`w-12 h-12 rounded-2xl bg-${kpi.color}-500/10 flex items-center justify-center text-${kpi.color}-500 border border-${kpi.color}-500/20 mb-4 shadow-inner`}>
                                {kpi.icon}
                            </div>
                            <div className="text-3xl font-black text-foreground tracking-tighter tabular-nums leading-none">{kpi.value}</div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mt-2 opacity-60">{kpi.label}</div>
                            <div className={`text-[10px] font-bold text-${kpi.color}-500 mt-1 flex items-center gap-1`}>
                                {kpi.label === 'This Month' && (growth >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />)}
                                {kpi.sub}
                            </div>
                        </Card>
                    </motion.div>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                <Card className="p-10 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem] shadow-2xl">
                    <div className="mb-10 flex justify-between items-start">
                        <div>
                            <h3 className="text-2xl font-black text-foreground tracking-tight">Monthly User Growth</h3>
                            <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest opacity-60 mt-1">Acquisition over the last 12 months</p>
                        </div>
                        <Badge className="bg-primary/10 text-primary border-primary/20 rounded-xl px-4 py-1.5 font-black uppercase tracking-widest text-[10px]">
                            Active Trend
                        </Badge>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={monthlyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5} />
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="5 5" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                                <YAxis allowDecimals={false} tick={{ fill: 'var(--text-muted)', fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                                <Tooltip {...tooltipStyle} />
                                <Area
                                    type="monotone"
                                    dataKey="Users"
                                    stroke="#8b5cf6"
                                    strokeWidth={3}
                                    fill="url(#userGrad)"
                                    dot={{ r: 5, fill: '#8b5cf6', strokeWidth: 2, stroke: '#1c1c21' }}
                                    activeDot={{ r: 8, strokeWidth: 2, stroke: '#1c1c21', fill: '#a78bfa' }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <div className="grid grid-cols-1 gap-8">
                    <Card className="p-8 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem] flex flex-col">
                        <div className="mb-8 overflow-hidden">
                            <h3 className="text-xl font-black text-foreground tracking-tight">Daily Performance</h3>
                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-60 mt-1">User activity last 14 days</p>
                        </div>
                        <div className="flex-1 min-h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={dailyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#22d3ee" stopOpacity={1} />
                                            <stop offset="100%" stopColor="#0891b2" stopOpacity={0.8} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                    <XAxis dataKey="day" tick={{ fill: 'var(--text-muted)', fontSize: 9, fontWeight: 'bold' }} axisLine={false} tickLine={false} interval={1} />
                                    <YAxis allowDecimals={false} tick={{ fill: 'var(--text-muted)', fontSize: 9, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                                    <Tooltip {...tooltipStyle} />
                                    <Bar dataKey="Users" fill="url(#barGrad)" radius={[8, 8, 0, 0]} maxBarSize={30} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    <Card className="p-8 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem] flex flex-col">
                        <div className="mb-8 overflow-hidden">
                            <h3 className="text-xl font-black text-foreground tracking-tight">Newsletter Traction</h3>
                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-60 mt-1">Subscriber conversions per month</p>
                        </div>
                        <div className="flex-1 min-h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={subMonthlyData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fill: 'var(--text-muted)', fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                                    <Tooltip {...tooltipStyle} />
                                    <Line type="step" dataKey="Subscribers" stroke="#f59e0b" strokeWidth={4} dot={{ r: 6, fill: '#f59e0b', strokeWidth: 2, stroke: '#1c1c21' }} activeDot={{ r: 8, stroke: '#1c1c21', fill: '#fbbf24' }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="p-10 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem]">
                    <div className="mb-8">
                        <h3 className="text-2xl font-black text-foreground tracking-tight">Identity Distribution</h3>
                        <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest opacity-60 mt-1">Platform user role segmentation</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-12">
                        <div className="relative w-48 h-48">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={roleData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={8} dataKey="value" stroke="none">
                                        {roleData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip {...tooltipStyle} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <div className="text-2xl font-black text-foreground tracking-tighter tabular-nums leading-none">{users.length}</div>
                                <div className="text-[8px] font-black uppercase tracking-widest text-muted-foreground opacity-40">Users</div>
                            </div>
                        </div>
                        <div className="space-y-4 flex-1 max-w-[200px]">
                            {roleData.map(r => (
                                <div key={r.name} className="flex items-center justify-between group">
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 rounded-full shadow-lg" style={{ background: r.color }} />
                                        <span className="text-xs font-black text-muted-foreground uppercase tracking-widest opacity-70 group-hover:opacity-100 transition-opacity">{r.name}</span>
                                    </div>
                                    <span className="text-sm font-black text-foreground tabular-nums">{r.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>

                <Card className="p-10 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem]">
                    <div className="mb-8">
                        <h3 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-3">
                            <Shield className="text-primary" /> Platform Health
                        </h3>
                        <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest opacity-60 mt-1">Core system metrics & statistics</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            { label: 'Registered Today', value: todayUsers, color: 'text-emerald-500' },
                            { label: 'Monthly Active', value: monthUsers, color: 'text-cyan-500' },
                            { label: 'Database Objects', value: users.length + subscribers.length + tutorials.length + contacts.length + notifications.length, color: 'text-violet-500' },
                            { label: 'Identity Protection', value: 'Level 4', color: 'text-amber-500' },
                        ].map((stat, i) => (
                            <div key={i} className="p-6 rounded-2xl bg-secondary/20 border border-border/40 space-y-1 hover:bg-secondary/30 transition-colors">
                                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-50">{stat.label}</div>
                                <div className={`text-xl font-black tracking-tight ${stat.color}`}>{stat.value}</div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </motion.div>
    );
};

export default AnalyticsSection;
